import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { getDeviceInfo, DeviceInfo } from '../utils/deviceFingerprint'

export interface RiskFactor {
  type: string
  score: number
  description: string
  count?: number
}

export interface RiskScore {
  score: number
  factors: RiskFactor[]
  requires_captcha: boolean
}

export interface CaptchaConfig {
  enabled: boolean
  provider: 'turnstile'
  site_key: string | null
  secret_key: string | null
  risk_threshold: number
}

export function useRiskEngine() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [riskScore, setRiskScore] = useState<RiskScore | null>(null)
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null)

  // Get current device fingerprint
  const getFingerprint = useCallback((): DeviceInfo => {
    const info = getDeviceInfo()
    setDeviceInfo(info)
    return info
  }, [])

  // Calculate risk score for a login attempt
  const calculateRisk = useCallback(async (
    identifier: string,
    ipAddress?: string,
    userId?: string
  ): Promise<RiskScore | null> => {
    setLoading(true)
    setError(null)

    try {
      // Get device fingerprint
      const device = getDeviceInfo()
      setDeviceInfo(device)

      const { data, error: rpcError } = await supabase.rpc('calculate_risk_score', {
        p_identifier: identifier,
        p_ip_address: ipAddress || 'unknown',
        p_user_id: userId || null,
        p_device_fingerprint: device.fingerprint,
        p_browser: device.browser,
        p_os: device.os
      })

      if (rpcError) throw rpcError

      const result = data as RiskScore
      setRiskScore(result)
      return result
    } catch (err: any) {
      console.error('Error calculating risk score:', err)
      setError(err.message)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  // Get captcha config for an application
  const getCaptchaConfig = useCallback(async (appId: string): Promise<CaptchaConfig | null> => {
    try {
      const { data, error: queryError } = await supabase
        .from('applications')
        .select('captcha_config')
        .eq('id', appId)
        .single()

      if (queryError) throw queryError
      return data?.captcha_config as CaptchaConfig
    } catch (err: any) {
      console.error('Error getting captcha config:', err)
      return null
    }
  }, [])

  // Verify Turnstile token (client-side call to edge function)
  const verifyCaptcha = useCallback(async (
    token: string,
    secretKey: string
  ): Promise<boolean> => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('verify-captcha', {
        body: {
          token,
          secret_key: secretKey
        }
      })

      if (fnError) throw fnError
      return data?.success === true
    } catch (err: any) {
      console.error('Error verifying captcha:', err)
      setError(err.message)
      return false
    }
  }, [])

  // Check if captcha is required based on risk score and app config
  const shouldShowCaptcha = useCallback((
    riskScore: RiskScore | null,
    captchaConfig: CaptchaConfig | null
  ): boolean => {
    if (!captchaConfig?.enabled) return false
    if (!captchaConfig.site_key) return false
    if (!riskScore) return false

    return riskScore.score >= (captchaConfig.risk_threshold || 50)
  }, [])

  // Get risk level description
  const getRiskLevel = useCallback((score: number): 'low' | 'medium' | 'high' | 'critical' => {
    if (score < 25) return 'low'
    if (score < 50) return 'medium'
    if (score < 75) return 'high'
    return 'critical'
  }, [])

  // Get human-readable risk description
  const getRiskDescription = useCallback((score: number): string => {
    const level = getRiskLevel(score)
    switch (level) {
      case 'low':
        return 'Low risk - Normal login attempt'
      case 'medium':
        return 'Medium risk - Some unusual activity detected'
      case 'high':
        return 'High risk - Verification required'
      case 'critical':
        return 'Critical risk - Multiple risk factors detected'
    }
  }, [getRiskLevel])

  return {
    loading,
    error,
    riskScore,
    deviceInfo,
    calculateRisk,
    getFingerprint,
    getCaptchaConfig,
    verifyCaptcha,
    shouldShowCaptcha,
    getRiskLevel,
    getRiskDescription
  }
}
